import apiClient from '@/lib/api-client';

const GLN_PATTERN = /^\d{13}$/;

function validateGln(gln: string): string {
  if (!GLN_PATTERN.test(gln)) {
    throw new Error(`Invalid GLN format: ${gln}`);
  }
  return encodeURIComponent(gln);
}

export interface ValueSavingsResponse {
  clientGln: string;
  quarter: string;
  totalProducts: number;
  totalSavingEur: number;
  hoursSaved: number;
  timeSaving: {
    hoursWithoutXxtract: number;
    hoursWithXxtract: number;
    hoursSavedPerYear: number;
    hoursSavedPerQuarter: number;
  };
  costSaving: {
    costWithoutXxtract: number;
    costWithXxtract: number;
    savingPerYear: number;
    savingPerQuarter: number;
  };
  risksCovered: {
    count: number;
    items: string[];
  };
  timeToMarket: {
    withoutXxtractWeeks: string;
    withXxtractDays: number;
    productsLaunchedThisQuarter: number;
  };
  parameters: {
    hourlyRate: number;
    hoursPerProduct: number;
    xxtractHoursPerProduct: number;
    xxtractAnnualCost: number;
    isDefault: boolean;
  };
}

export interface ValueComplianceResponse {
  clientGln: string;
  ppwr: {
    status: 'prepared' | 'mostly_prepared' | 'action_needed';
    packInfoScore: number;
    deadlineDate: string;
  };
  gs1: {
    currentVersion: number;
    latestUpdate: string;
    fieldsAffected: number;
    status: 'up_to_date' | 'update_available';
  };
  retailerProfiles: Array<{ id: number; retailerName: string; active: boolean }>;
  overallStatus: 'compliant' | 'attention_needed' | 'action_required';
  risksCoveredCount: number;
}

export interface ValueBenchmarkResponse {
  clientGln: string;
  available: boolean;
  overallPercentile: number;
  overallScore: number;
  marketAverageOverall: number;
  clientCount: number;
  categoryPercentiles: Array<{
    categoryId: string;
    categoryName: string;
    clientScore: number;
    percentile: number;
    marketAverage: number;
  }>;
}

export interface ActivitySummaryResponse {
  product_launched: number;
  change_processed: number;
  retailer_question: number;
  gs1_update: number;
  legislation_change: number;
  packaging_update: number;
  total: number;
}

export async function getValueSavings(gln: string): Promise<{ data: ValueSavingsResponse }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.get<{ data: ValueSavingsResponse }>(
    `/value/${safeGln}/savings`,
  );
  return response.data;
}

export async function getValueCompliance(gln: string): Promise<{ data: ValueComplianceResponse }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.get<{ data: ValueComplianceResponse }>(
    `/value/${safeGln}/compliance`,
  );
  return response.data;
}

export async function getValueBenchmark(gln: string): Promise<{ data: ValueBenchmarkResponse }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.get<{ data: ValueBenchmarkResponse }>(
    `/value/${safeGln}/benchmark`,
  );
  return response.data;
}

// --- Incompleteness Cost ---

export interface IncompleteCostResponse {
  clientGln: string;
  totalEstimatedCost: number;
  totalMissingFields: number;
  productsAtRisk: number;
  totalProducts: number;
  timeToMarketImpact: {
    productsDelayed: number;
    estimatedDelayDays: number;
  };
  categories: Array<{
    categoryId: string;
    missingFields: number;
    affectedProducts: number;
    estimatedCost: number;
    riskLevel: 'high' | 'medium' | 'low';
    applicable: boolean;
  }>;
  topProducts: Array<{
    id: string;
    name: string;
    gtin: string;
    score: number;
    missingFieldCount: number;
    estimatedCost: number;
    missingCategories: string[];
  }>;
  parameters: {
    hourlyRate: number;
    costPerField: number;
    riskMultipliers: Record<string, number>;
  };
}

export async function getIncompleteCost(gln: string): Promise<{ data: IncompleteCostResponse }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.get<{ data: IncompleteCostResponse }>(
    `/value/${safeGln}/incomplete-cost`,
  );
  return response.data;
}

// --- Activity types ---

export type ActivityType =
  | 'product_launched'
  | 'change_processed'
  | 'retailer_question'
  | 'gs1_update'
  | 'legislation_change'
  | 'packaging_update';

export interface Activity {
  id: number;
  clientGln: string;
  type: ActivityType;
  title: string;
  description: string | null;
  productName: string | null;
  productGtin: string | null;
  retailers: string[] | null;
  fieldsAffected: number | null;
  createdAt: string;
  createdBy: string | null;
}

export interface PaginatedActivities {
  data: Activity[];
  meta: { total: number; page: number; pageSize: number };
}

// --- Savings params ---

export interface SavingsParamsInput {
  hourlyRate?: number;
  hoursPerProduct?: number;
  xxtractHoursPerProduct?: number;
  xxtractAnnualCost?: number;
}

// --- Management Summary ---

export interface ManagementSummaryData {
  clientGln: string;
  companyName: string;
  quarter: string;
  generatedAt: string;
  snelInHetSchap: {
    productsLaunchedThisQuarter: number;
    averageLeadTimeDays: number;
    selfDoLeadTimeWeeks: string;
  };
  aanAlleEisenVoldaan: {
    gs1UpdateProcessed: boolean;
    gs1FieldsAffected: number;
    ppwrStatus: string;
    ppwrDeadlineDate: string;
    retailerProfilesActive: number;
  };
  altijdCorrect: {
    totalProductsManaged: number;
    blockagesPrevented: number;
    retailerQuestionsResolved: number;
    overallCompletenessScore: number;
  };
  besparing: {
    hoursSavedPerQuarter: number;
    costSavedPerQuarter: number;
    totalSavingEur: number;
  };
  benchmark: {
    available: boolean;
    overallPercentile: number;
    marketAverage: number;
  };
}

// --- Quarterly Reports ---

export interface QuarterlyReportClient {
  clientGln: string;
  companyName: string;
  config: {
    clientGln: string;
    recipientName: string;
    recipientEmail: string;
    enabled: boolean;
    updatedAt: string;
  } | null;
  status: {
    quarter: string;
    status: 'pending' | 'generated' | 'sent' | 'failed' | 'not_configured';
    generatedAt: string | null;
    sentAt: string | null;
  };
}

export interface ComplianceDeadline {
  id: number;
  clientGln: string;
  type: string;
  title: string;
  deadlineDate: string;
  description: string | null;
  status: 'on_track' | 'attention_needed' | 'overdue';
  productCount: number | null;
  categoryFilter: string | null;
  createdAt: string;
}

// --- API functions ---

export async function getActivities(gln: string, params?: {
  period?: string;
  date?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedActivities> {
  const safeGln = validateGln(gln);
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  if (params?.date) searchParams.set('date', params.date);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  const qs = searchParams.toString();
  const response = await apiClient.get<PaginatedActivities>(
    `/value/${safeGln}/activities${qs ? `?${qs}` : ''}`,
  );
  return response.data;
}

export async function getActivitySummary(gln: string, period?: string): Promise<{ data: ActivitySummaryResponse }> {
  const safeGln = validateGln(gln);
  const params = new URLSearchParams();
  if (period) params.set('period', period);
  const qs = params.toString();
  const response = await apiClient.get<{ data: ActivitySummaryResponse }>(
    `/value/${safeGln}/activities/summary${qs ? `?${qs}` : ''}`,
  );
  return response.data;
}

export async function updateSavingsParams(gln: string, input: SavingsParamsInput): Promise<{ data: ValueSavingsResponse['parameters'] }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.put<{ data: ValueSavingsResponse['parameters'] }>(
    `/value/${safeGln}/savings/params`,
    input,
  );
  return response.data;
}

export async function getComplianceDeadlines(gln: string): Promise<{ data: ComplianceDeadline[] }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.get<{ data: ComplianceDeadline[] }>(
    `/value/${safeGln}/compliance/deadlines`,
  );
  return response.data;
}

export async function getManagementSummary(gln: string): Promise<{ data: ManagementSummaryData }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.get<{ data: ManagementSummaryData }>(
    `/value/${safeGln}/summary`,
  );
  return response.data;
}

export async function downloadSummaryPdf(gln: string): Promise<Blob> {
  const safeGln = validateGln(gln);
  const response = await apiClient.post(
    `/value/${safeGln}/summary/pdf`,
    {},
    { responseType: 'blob' },
  );
  return response.data as Blob;
}

export async function createShareLink(gln: string): Promise<{ data: { url: string; expiresAt: string } }> {
  const safeGln = validateGln(gln);
  const response = await apiClient.post<{ data: { url: string; expiresAt: string } }>(
    `/value/${safeGln}/summary/share`,
  );
  return response.data;
}

export async function getQuarterlyReports(quarter?: string): Promise<{ data: QuarterlyReportClient[] }> {
  const searchParams = new URLSearchParams();
  if (quarter) searchParams.set('quarter', quarter);
  const qs = searchParams.toString();
  const response = await apiClient.get<{ data: QuarterlyReportClient[] }>(
    `/reports/quarterly${qs ? `?${qs}` : ''}`,
  );
  return response.data;
}

export async function updateReportConfig(gln: string, config: { recipientName: string; recipientEmail: string }): Promise<void> {
  const safeGln = validateGln(gln);
  await apiClient.put(`/reports/quarterly/${safeGln}/config`, config);
}

export async function sendReportApi(gln: string): Promise<void> {
  const safeGln = validateGln(gln);
  await apiClient.post(`/reports/quarterly/${safeGln}/send`);
}

export async function generateAllReportsApi(quarter?: string): Promise<{ data: { total: number; success: number; failed: number } }> {
  const searchParams = new URLSearchParams();
  if (quarter) searchParams.set('quarter', quarter);
  const qs = searchParams.toString();
  const response = await apiClient.post<{ data: { total: number; success: number; failed: number } }>(
    `/reports/quarterly/generate-all${qs ? `?${qs}` : ''}`,
  );
  return response.data;
}
